import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from './config.service';

export interface Domain {
  domainID: number;
  userAccountID: number;
  databaseName: string;
  databaseUsername: string;
  databasePassword: string;
  domainGroups?: number;
  domainUsers?: number;
  domainName: string;
  domainColors?: string;
  domainWcfPassword?: string;
  domainAPIKey?: string;
  userLogo?: string;
  userBackground?: string;
  userLogoSmall?: string;
  useExternalWorkOrders?: boolean;
  useStockIntegration?: boolean;
  useBugReport?: boolean;
  useGlobalSearch?: boolean;
  isMonitoringEnabled?: boolean;
  erpClientNumber?: string;
}

export interface Module {
  moduleID: number;
  moduleName: string;
  moduleVersion?: string;
  moduleDescription?: string;
  moduleTemplate?: string;
}

export interface ModuleActivation {
  moduleID: number;
  moduleName: string;
  isActive: boolean;
  internet: boolean;
  intranet: boolean;
  extranet: boolean;
}

export interface DomainWithModules extends Domain {
  modules: ModuleActivation[];
}

export interface SaveDomainRequest {
  domainID?: number;
  userAccountID: number;
  databaseName: string;
  databaseUsername: string;
  databasePassword: string;
  domainName: string;
  domainColors?: string;
  userLogo?: string;
  userBackground?: string;
  userLogoSmall?: string;
  useExternalWorkOrders?: boolean;
  useStockIntegration?: boolean;
  useBugReport?: boolean;
  useGlobalSearch?: boolean;
  isMonitoringEnabled?: boolean;
  erpClientNumber?: string;
  domainUsers?: number;
  domainGroups?: number;
  activeModuleIDs: number[];
}

export interface DomainNotificationConfig {
  configID: number;
  domainName: string;
  isActive: boolean;
  checkIntervalMinutes: number;
  lastCheckedTime?: Date;
  createdDate: Date;
  modifiedDate?: Date;
  enableNotifications: boolean;
  enableTimeScheduler: boolean;
  enableIntegrations: boolean;
  enableCrmTaskReminders: boolean;
  enableCrmAssignmentReminders: boolean;
  enableCrmAgreementExpiry: boolean;
  enableChecklistReminders: boolean;
  enableDeviationReminders: boolean;
  enableProjectTaskReminders: boolean;
  enableGoalReminders: boolean;
  enableRiskReviewReminders: boolean;
  enableBankReconciliation: boolean;
}

export interface SaveDomainNotificationConfigRequest {
  domainName: string;
  isActive: boolean;
  checkIntervalMinutes: number;
  enableNotifications: boolean;
  enableTimeScheduler: boolean;
  enableIntegrations: boolean;
  enableCrmTaskReminders: boolean;
  enableCrmAssignmentReminders: boolean;
  enableCrmAgreementExpiry: boolean;
  enableChecklistReminders: boolean;
  enableDeviationReminders: boolean;
  enableProjectTaskReminders: boolean;
  enableGoalReminders: boolean;
  enableRiskReviewReminders: boolean;
  enableBankReconciliation: boolean;
}

export interface OAuthIntegration {
  mappingId?: number;
  domainID?: number;
  domainName?: string;
  integrationName?: string;
  description?: string;
  azureTenantId: string;
  azureClientId: string;
  domainDB: string;
  domainDBUser?: string;
  domainDBPassword?: string;
  isActive: boolean;
  lastUsedDate?: string;
  createdDate?: string;
  createdBy?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

export interface SaveOAuthIntegrationRequest {
  mappingId?: number;
  domainID: number;
  integrationName?: string;
  description?: string;
  azureTenantId: string;
  azureClientId: string;
  domainDB: string;
  domainDBUser?: string;
  domainDBPassword?: string;
  isActive: boolean;
}

export interface TestOAuthConnectionRequest {
  azureTenantId: string;
  azureClientId: string;
  domainDB: string;
  domainDBUser?: string;
  domainDBPassword?: string;
}

export interface OAuthConnectionTestResult {
  azureAuthSuccess: boolean;
  azureAuthMessage?: string;
  databaseConnectionSuccess: boolean;
  databaseConnectionMessage?: string;
  overallSuccess: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SystemDomainService {
  private get apiUrl(): string {
    return `${this.configService.get('apiBaseUrl')}/SystemDomain`;
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) { }

  getAllDomains(): Observable<Domain[]> {
    return this.http.get<any>(`${this.apiUrl}`).pipe(
      map((response: any) => response.result || [])
    );
  }

  getDomainById(domainId: number): Observable<Domain> {
    return this.http.get<any>(`${this.apiUrl}/${domainId}`).pipe(
      map((response: any) => response.result)
    );
  }

  getDomainWithModules(domainId: number): Observable<DomainWithModules> {
    return this.http.get<any>(`${this.apiUrl}/${domainId}/WithModules`).pipe(
      map((response: any) => response.result)
    );
  }

  createDomain(request: SaveDomainRequest): Observable<number> {
    return this.http.post<any>(`${this.apiUrl}`, request).pipe(
      map((response: any) => response.result)
    );
  }

  updateDomain(domainId: number, request: SaveDomainRequest): Observable<boolean> {
    return this.http.put<any>(`${this.apiUrl}/${domainId}`, request).pipe(
      map((response: any) => response.result)
    );
  }

  deleteDomain(domainId: number): Observable<boolean> {
    return this.http.delete<any>(`${this.apiUrl}/${domainId}`).pipe(
      map((response: any) => response.result)
    );
  }

  updateDomainModules(domainId: number, moduleIds: number[]): Observable<boolean> {
    return this.http.put<any>(`${this.apiUrl}/${domainId}/Modules`, moduleIds).pipe(
      map((response: any) => response.result)
    );
  }

  regenerateAPIKey(domainId: number): Observable<string> {
    return this.http.post<any>(`${this.apiUrl}/${domainId}/RegenerateApiKey`, null).pipe(
      map((response: any) => response.result)
    );
  }

  getDomainNotificationConfig(domainName: string): Observable<DomainNotificationConfig> {
    return this.http.get<any>(`${this.apiUrl}/${domainName}/NotificationConfig`).pipe(
      map((response: any) => response.result)
    );
  }

  saveDomainNotificationConfig(request: SaveDomainNotificationConfigRequest): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/NotificationConfig`, request).pipe(
      map((response: any) => response.result)
    );
  }

  // OAuth Integration Management
  getOAuthIntegration(domainId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${domainId}/oauth`).pipe(
      map((response: any) => response)
    );
  }

  saveOAuthIntegration(domainId: number, request: SaveOAuthIntegrationRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${domainId}/oauth`, request).pipe(
      map((response: any) => response)
    );
  }

  testOAuthConnection(domainId: number, request: TestOAuthConnectionRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${domainId}/oauth/test`, request).pipe(
      map((response: any) => response)
    );
  }
}
